import { Controller } from "@hotwired/stimulus"

// The wall screen refreshes itself.
//
// Polling, not a broadcast: a screen that hangs for months on an office wall
// outlives websockets, sleeping televisions and every deploy, and the one
// thing it must never do is show a stale day as if it were today. The same
// reasoning the report page settled on (report_polling_controller).
export default class extends Controller {
  static targets = ["board", "clock", "bell"]
  static values = { url: String, interval: { type: Number, default: 15000 } }

  connect() {
    this.version = this.element.dataset.screenVersion
    this.start()
    this.tickClock()
    this.clockTimer = setInterval(() => this.tickClock(), 1000)
    this.visibility = () => (document.hidden ? this.stop() : this.start({ now: true }))
    document.addEventListener("visibilitychange", this.visibility)
    this.watchBell()
  }

  disconnect() {
    this.bellWatcher?.disconnect()
    this.stop()
    clearInterval(this.clockTimer)
    document.removeEventListener("visibilitychange", this.visibility)
  }

  // An order webhook updates the bell over the Turbo stream. Whatever it
  // says does not matter — that it changed does. The board is then re-read
  // the same way the poll reads it, so there is one path to one board.
  watchBell() {
    if (!this.hasBellTarget) return

    this.bellWatcher = new MutationObserver(() => this.refresh())
    this.bellWatcher.observe(this.bellTarget, { childList: true, characterData: true, subtree: true })
  }

  // The server has just drawn the board, so the first tick waits out the
  // interval. Waking from a sleeping television does not: that board is as
  // old as the sleep was long.
  start({ now = false } = {}) {
    if (this.timer) return
    this.timer = setInterval(() => this.refresh(), this.intervalValue)
    if (now) this.refresh()
  }

  stop() {
    if (!this.timer) return
    clearInterval(this.timer)
    this.timer = null
  }

  // The poll sends back the version it is showing. Unchanged, the server
  // answers 204 and there is nothing to draw — which is what almost every
  // poll is, on almost every screen, almost all day.
  async refresh() {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      if (this.version) url.searchParams.set("v", this.version)

      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) return

      this.version = response.headers.get("X-Screen-Version") || this.version
      if (response.status === 204) return

      const html = await response.text()
      if (this.hasBoardTarget) this.boardTarget.innerHTML = html
    } catch {
      // A screen on a wall rides out a dropped network: keep the last good
      // board up and try again on the next tick.
    }
  }

  // The clock ticks between polls so the room never sees a frozen minute.
  tickClock() {
    this.clockTargets.forEach((element) => {
      element.textContent = new Intl.DateTimeFormat(document.documentElement.lang || "en", {
        hour: "2-digit",
        minute: "2-digit",
        // The server draws %H:%M; a browser left to its own locale would
        // put the wall clock on AM/PM every other refresh.
        hourCycle: "h23",
        timeZone: element.dataset.screenZone || undefined
      }).format(new Date())
    })
  }

  fullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      document.documentElement.requestFullscreen?.()
    }
  }

  copyLink(event) {
    const link = event.params.link || event.currentTarget.dataset.screenLinkParam
    navigator.clipboard?.writeText(link)
    const button = event.currentTarget
    const original = button.textContent
    button.textContent = button.dataset.copiedLabel || "Copied"
    setTimeout(() => (button.textContent = original), 1500)
  }
}
